import { useSyncExternalStore } from "react";
import type { PageKey } from "./components/PageSkel";

/**
 * 更新了什么: version by version, so everybody on the team can see what got
 * better (owner 10/17, of the prototype: 「拿掉了其他人怎么知道优化了什么？」).
 *
 * This file is only the list of versions — what the menu's 「更新了什么 v… 新」
 * reads — and comes with the console. What each version brought, and its
 * pictures, are in newsLog.ts, downloaded when the drawer opens, so a log
 * with pictures (owner 10/27: 「你显示的全是字 谁会想看？能不能生动有趣一点？」)
 * costs the console's first opening nothing. A version goes in both: its
 * line here, its contents there under the same `v`.
 *
 * The newest version goes first. Its date is the day it reached
 * sales.lazior.com — not the day it was written: 3.0 said 10月23日, the day
 * the redesign was begun, and went live on 10月27日.
 */
export interface NewsHead {
  v: string;
  date: string;
  /** The version in a line. */
  say: string;
}

/** Never empty: the newest version, then the ones before it. */
export const NEWS = [
  { v: "3.4", date: "10月29日", say: "同事按的开关几秒就同步，小爱素材库看得到整张图，经营快很多" },
  { v: "3.3", date: "10月28日", say: "大屏广告颁奖台换成广告图，选了别的日子来单也掉进猪扑满" },
  { v: "3.2", date: "10月28日", say: "大屏每屏选一段日子，对话能按产品、按广告找客户" },
  { v: "3.1", date: "10月27日", say: "大屏每一屏能选日期，经营的数字算得更准" },
  { v: "3.0", date: "10月27日", say: "新版 CRM 上线：新的样子，新的一页「今天」" },
] as const satisfies readonly [NewsHead, ...NewsHead[]];

export type NewsV = (typeof NEWS)[number]["v"];

/** Where 去看看 goes: a page, or the meeting room's screen. */
export type NewsTo = PageKey | "live";

export const NEWS_VER = NEWS[0].v;

const SEEN_KEY = "lazior_news_seen";
/** The version last opened on this browser, read once. */
let seenVer: string | null | undefined;
const listeners = new Set<() => void>();

function readSeenVer(): string | null {
  if (seenVer === undefined) {
    try {
      seenVer = localStorage.getItem(SEEN_KEY);
    } catch {
      seenVer = NEWS_VER; // no storage: never nag
    }
  }
  return seenVer;
}

const readSeen = () => readSeenVer() === NEWS_VER;

/** The newest version has not been opened on this browser yet: 「新」 beside 更新了什么. */
export function useNewsFresh(): boolean {
  return !useSyncExternalStore(
    (f) => {
      listeners.add(f);
      return () => {
        listeners.delete(f);
      };
    },
    readSeen,
  );
}

/**
 * The versions this browser has not opened yet, newest first — read before
 * markNewsSeen(), so the drawer can mark them 「新」 the one time it shows them.
 * Never opened at all: only the newest, not the whole history.
 */
export function newsUnseen(): Set<string> {
  const last = readSeenVer();
  const at = NEWS.findIndex((n) => n.v === last);
  return new Set(NEWS.slice(0, at < 0 ? 1 : at).map((n) => n.v));
}

export function markNewsSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, NEWS_VER);
  } catch {
    /* this visit only */
  }
  seenVer = NEWS_VER;
  for (const f of listeners) f();
}
